import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth } from '../firebase';
import { 
  signInWithPhoneNumber, 
  RecaptchaVerifier,
  onAuthStateChanged,
  signOut 
} from 'firebase/auth';
import { authService } from '../services';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmationResult, setConfirmationResult] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Auth state changed:', user ? user.uid : 'No user');
      setCurrentUser(user);
      
      if (user) {
        try {
          // Get ID token
          const token = await user.getIdToken();
          localStorage.setItem('authToken', token);
          console.log('✅ Got auth token');
          
          // Fetch user profile
          console.log('📡 Fetching user profile from API...');
          const profile = await authService.getProfile();
          console.log('👤 User profile received:', profile);
          setUserProfile(profile.user);
          console.log('🎭 User role:', profile.user?.role);
          console.log('🏪 Shop ID:', profile.user?.shopId);
        } catch (error) {
          console.error('❌ Error fetching profile:', error);
          console.log('💡 Trying to fetch directly from Firestore...');
          
          // Fallback: Fetch directly from Firestore if API fails
          try {
            const { db } = await import('../firebase');
            const { doc, getDoc } = await import('firebase/firestore');
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              console.log('✅ Fetched from Firestore:', userData);
              setUserProfile({
                userId: user.uid,
                ...userData
              });
            } else {
              console.error('❌ User document not found in Firestore');
            }
          } catch (firestoreError) {
            console.error('❌ Firestore fetch also failed:', firestoreError);
          }
        }
      } else {
        localStorage.removeItem('authToken');
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA solved
        }
      });
    }
  };

  const sendOTP = async (phoneNumber) => {
    try {
      setupRecaptcha();
      const appVerifier = window.recaptchaVerifier;
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(confirmation);
      return { success: true };
    } catch (error) {
      console.error('Error sending OTP:', error);
      return { success: false, error: error.message };
    }
  };

  const verifyOTP = async (code, mobile) => {
    try {
      if (!confirmationResult) {
        throw new Error('No confirmation result. Please request OTP first.');
      }
      
      const result = await confirmationResult.confirm(code);
      const user = result.user;
      
      // Verify with backend
      await authService.verifyOTP(mobile, user.uid);
      
      return { success: true };
    } catch (error) {
      console.error('Error verifying OTP:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('authToken');
      setUserProfile(null);
      return { success: true };
    } catch (error) {
      console.error('Error logging out:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    sendOTP,
    verifyOTP,
    logout,
    isAuthenticated: !!currentUser,
    isSuperAdmin: userProfile?.role === 'super-admin',
    isShopAdmin: userProfile?.role === 'shop-admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
